import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Popover,
  Typography
} from "@mui/material";
import { useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

import { BorderSide } from "~/features/andon/andon.types";
import { useAndonAdvancedFeatures } from "~/features/andon/andonBoard/hooks/useAndonAdvancedFeatures";
import { useAndonWorkstationsByFcIds } from "~/hooks/useAndonWorkstationsByFcIds";
import { groupArrayByKey } from "~/lib/helpers";
import { useGetAllFulfillmentCentersQuery } from "~/redux/warehouse/fulfillmentCenter.hooks";
import { AndonWorkstation } from "~/types/api";

import CellBorderControls from "./CellBorderControls";

type WorkstationGridListProps = {
  workstations: AndonWorkstation[];
  workingWorkstationIds: string[];
  onSelect: (workstationId: string) => void;
};

function WorkstationGridList({
  workstations,
  workingWorkstationIds,
  onSelect
}: WorkstationGridListProps) {
  const { t } = useTranslation();
  const available = workstations.filter(
    (ws) => !workingWorkstationIds.includes(ws.workstationId)
  );

  if (available.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1 }}>
        {t("no more workstations to add")}
      </Typography>
    );
  }

  const grids = groupArrayByKey(available, (ws) => ws.autostoreGridId);
  const gridIds = Object.keys(grids).sort((a, b) =>
    grids[a][0].autostoreGridName.localeCompare(grids[b][0].autostoreGridName)
  );

  return (
    <>
      {gridIds.map((gridId) => {
        const gridWorkstations = grids[gridId]
          .slice()
          .sort((a, b) => a.deviceId.localeCompare(b.deviceId));
        return (
          <Box key={gridId}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ px: 2, mt: 0.5, display: "block" }}
            >
              {gridWorkstations[0].autostoreGridName}
            </Typography>
            {gridWorkstations.map((ws) => (
              <MenuItem
                key={ws.workstationId}
                dense
                onClick={() => onSelect(ws.workstationId)}
              >
                <ListItemText primary={ws.deviceId} />
              </MenuItem>
            ))}
          </Box>
        );
      })}
    </>
  );
}

type FcWorkstationFetchListProps = {
  fcId?: string;
  workingWorkstationIds: string[];
  onSelect: (workstationId: string) => void;
};

function FcWorkstationFetchList({
  fcId,
  workingWorkstationIds,
  onSelect
}: FcWorkstationFetchListProps) {
  const { workstations: fetchedWorkstations, isError } =
    useAndonWorkstationsByFcIds(fcId ? [fcId] : [], { poll: false });
  const workstations = fetchedWorkstations ?? [];

  if (!fetchedWorkstations && !isError) {
    return null;
  }

  return (
    <WorkstationGridList
      workstations={workstations}
      workingWorkstationIds={workingWorkstationIds}
      onSelect={onSelect}
    />
  );
}

type AndonCellEditPopoverProps = {
  open: boolean;
  anchorPosition: { top: number; left: number } | null;
  onClose: () => void;
  canRemove: boolean;
  selectionTitle?: string;
  showAddTextLabel: boolean;
  workingWorkstationIds: string[];
  siteFcId?: string;
  cellBorderSides?: BorderSide[];
  cellBorderColor?: string;
  onRemove: () => void;
  onSelectWorkstation: (workstationId: string, fcId?: string) => void;
  onAddTextLabel: () => void;
  onSetCellBorder?: (sides: BorderSide[], color: string) => void;
};

export default function AndonCellEditPopover({
  open,
  anchorPosition,
  onClose,
  canRemove,
  selectionTitle,
  showAddTextLabel,
  workingWorkstationIds,
  siteFcId,
  cellBorderSides = [],
  cellBorderColor = "",
  onRemove,
  onSelectWorkstation,
  onAddTextLabel,
  onSetCellBorder
}: AndonCellEditPopoverProps) {
  const { t } = useTranslation();
  const advanced = useAndonAdvancedFeatures();
  const { data: allFcs = [] } = useGetAllFulfillmentCentersQuery();

  const otherFcs = useMemo(
    () => allFcs.filter((fc) => fc.fulfillmentCenterId !== siteFcId),
    [allFcs, siteFcId]
  );

  const lastOpenMenuContentRef = useRef({
    canRemove,
    selectionTitle,
    showAddTextLabel,
    cellBorderSides,
    cellBorderColor
  });
  if (open) {
    lastOpenMenuContentRef.current = {
      canRemove,
      selectionTitle,
      showAddTextLabel,
      cellBorderSides,
      cellBorderColor
    };
  }
  const {
    canRemove: lastOpenCanRemove,
    selectionTitle: lastOpenSelectionTitle,
    showAddTextLabel: lastOpenShowAddTextLabel,
    cellBorderSides: lastOpenCellBorderSides,
    cellBorderColor: lastOpenCellBorderColor
  } = lastOpenMenuContentRef.current;

  // Remount the scroll body when the menu reopens on a new selection so scroll resets to top.
  const scrollBodyKey = `${selectionTitle ?? ""}:${anchorPosition?.left ?? 0}:${anchorPosition?.top ?? 0}`;

  return (
    <Popover
      open={open}
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition ?? undefined}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
    >
      <Box
        key={scrollBodyKey}
        aria-label={t("workstation picker")}
        sx={{ width: 320, maxHeight: 480, overflowY: "auto", py: 1 }}
      >
        {lastOpenSelectionTitle && (
          <Typography
            variant="subtitle2"
            sx={{ px: 2, pt: 0.5, pb: 1, fontWeight: 600 }}
          >
            {lastOpenSelectionTitle}
          </Typography>
        )}
        {lastOpenCanRemove && (
          <>
            <MenuItem onClick={onRemove} sx={{ color: "error.main" }}>
              <ListItemIcon sx={{ color: "error.main", minWidth: 36 }}>
                <DeleteOutlineIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={t("remove from board")} />
            </MenuItem>
            <Divider />
          </>
        )}

        {onSetCellBorder && (
          <>
            <CellBorderControls
              cellBorderSides={lastOpenCellBorderSides}
              cellBorderColor={lastOpenCellBorderColor}
              onSetCellBorder={onSetCellBorder}
            />
            <Divider />
          </>
        )}

        <Typography
          variant="overline"
          color="text.secondary"
          sx={{ px: 2, display: "block" }}
        >
          {lastOpenCanRemove ? t("replace with") : t("add workstation")}
        </Typography>

        <FcWorkstationFetchList
          fcId={siteFcId}
          workingWorkstationIds={workingWorkstationIds}
          onSelect={(id) => onSelectWorkstation(id, siteFcId)}
        />

        {lastOpenShowAddTextLabel && (
          <>
            <Divider />
            <MenuItem onClick={onAddTextLabel}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <TextFieldsIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={t("add text label")} />
            </MenuItem>
          </>
        )}

        {advanced && otherFcs.length > 0 && (
          <>
            <Divider sx={{ my: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {t("other fulfillment centers")}
              </Typography>
            </Divider>
            {otherFcs.map((fc) => (
              <Accordion
                key={fc.fulfillmentCenterId}
                disableGutters
                square
                sx={{ boxShadow: "none", "&:before": { display: "none" } }}
                TransitionProps={{ unmountOnExit: true }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">
                    {fc.abbreviation || fc.name}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  <FcWorkstationFetchList
                    fcId={fc.fulfillmentCenterId}
                    workingWorkstationIds={workingWorkstationIds}
                    onSelect={(id) =>
                      onSelectWorkstation(id, fc.fulfillmentCenterId)
                    }
                  />
                </AccordionDetails>
              </Accordion>
            ))}
          </>
        )}
      </Box>
    </Popover>
  );
}
